import styles from "./StudentTestResult.module.css"
import Back from "../components/Back"
import { Each } from "../common/Each"
import Card from "../components/cards/Card"
import { formatSeconds, toLetter } from "../utils"
import { useTranslation } from "react-i18next"
import { ReactComponent as CrossIcon } from "../assets/images/icons/ic-close.svg"
import { ReactComponent as CheckIcon } from "../assets/images/icons/ic-check.svg"
import { ReactComponent as ChatIcon } from "../assets/images/icons/ic-chat.svg"
import UserPlaceholder from "../assets/images/placeholders/user-placeholder.png"
import Button from "../components/Button"
import { useCallback, useMemo, useState } from "react"
import CircularProgress from "../components/CircularProgress"
import Tag from "../components/Tag"
import { TestType } from "../enums"
import TrueFalseBadge from "../components/tests/TrueFalseBadge"
import ReactMarkdown from "react-markdown"

const StudentTestResult = ({ student, test, successThreshold, onBack = () => { }, onContact = () => { } }) => {

  const { t } = useTranslation()
  const [circularContent, setCircularContent] = useState(null)
  const [threshold, setThreshold] = useState(successThreshold)
  const [showOnlyWrongQuestions, setShowOnlyWrongQuestions] = useState(false)

  const circularData = useMemo(() => {

    let correct = 0
    let wrong = 0
    let notAnswered = 0
    const successThreshold = threshold ?? 0
    for (const innerTest of test) {
      const { testType } = innerTest
      switch (testType) {
        case TestType.SingleChoice:
        case TestType.MultipleChoice: {
          let c = 0
          let w = 0
          let givenResponse = 0
          for (const answer of innerTest.answers) {
            const isStudentAnswer = answer.students.map((s) => s.id).includes(student.id)
            if (answer.isCorrect && isStudentAnswer) {
              c += 1
              givenResponse += 1
            } else if (!answer.isCorrect && isStudentAnswer) {
              w += 1
              givenResponse += 1
            }
          }
          correct += c
          wrong += w
          const nCorrect = innerTest.answers.filter(a => a.isCorrect).map(a => a.id).length
          if (nCorrect > givenResponse) {
            notAnswered += nCorrect - givenResponse
          }
        } break;

        case TestType.TrueFalse: {
          const { answers } = innerTest
          for (const answer of answers) {
            const { usersAnswers } = answer
            const userAnswer = usersAnswers.find(ua => ua.user.id === student.id)
            if (userAnswer?.isCorrect) {
              correct += 1
            } else if (!userAnswer?.isCorrect && userAnswer.value !== null) {
              wrong += 1
            } else {
              notAnswered += 1
            }
          }
        } break
        case TestType.TextCompletion: {
          const { words } = innerTest
          const unknowns = words.filter(w => w.hidden)
          for (const word of unknowns) {
            const { usersAnswers } = word
            const userAnswer = usersAnswers.find(ua => ua.user.id === student.id)
            if (userAnswer && userAnswer.isCorrect) {
              correct += 1
            } else if (userAnswer && userAnswer.isCorrect === false && userAnswer.answer !== "" && userAnswer.answer !== null) {
              wrong += 1
            } else {
              notAnswered += 1
            }
          }
        } break
        default: console.error("Unsupported test type"); break
      }
    }

    const sum = correct + wrong + notAnswered

    const pc = Math.round(((correct) / sum * 100) * 100) / 100
    const pw = Math.round(((wrong) / sum * 100) * 100) / 100
    const pna = Math.round(((notAnswered) / sum * 100) * 100) / 100

    if ((correct + wrong) === 0) {
      setCircularContent({
        label: t("tests.status.incomplete"), style: {
          fontSize: "1.125rem",
          fontWeight: 800,
          fill: "#B5B8BE"
        }
      })
    } else if (correct >= successThreshold) {
      setCircularContent({
        label: t("tests.status.passed"), style: {
          fontSize: "1.5rem",
          fontWeight: 800,
          fill: "var(--sf-light-green)"
        }
      })
    } else {
      setCircularContent({
        label: t("tests.status.failed"), style: {
          fontSize: "1.5rem",
          fontWeight: 800,
          fill: "var(--sf-red)"
        }
      })
    }

    return [
      {
        id: "correct",
        value: pc,
        rawValue: correct,
        color: 'var(--sf-light-green)',
        label: t("tests.correctAnswers")
      },
      {
        id: "wrong",
        value: pw,
        rawValue: wrong,
        color: 'var(--sf-red)',
        label: t("tests.wrongAnswers")
      },
      {
        id: "notAnswered",
        value: pna,
        rawValue: notAnswered,
        color: '#B5B8BE',
        label: t("tests.notAnswered")
      },
    ]
  }, [test])

  const studentAnalysis = useMemo(() => {
    const questionStats = []
    const tagStatsMap = new Map()
    const wrongQuestionIds = []

    const getQuestionTitle = (innerTest, index) => {
      const text = (innerTest.text || "").trim()
      return text.length > 0 ? text : `Domanda ${index + 1}`
    }

    for (let i = 0; i < test.length; i++) {
      const innerTest = test[i]
      let wrongCount = 0
      let totalCount = 0
      let isWrongQuestion = false

      if (innerTest.testType === TestType.SingleChoice || innerTest.testType === TestType.MultipleChoice) {
        const correctIds = (innerTest.answers || [])
          .filter(a => a.isCorrect)
          .map(a => a.id)
          .sort()
        const selectedIds = (innerTest.answers || [])
          .filter(a => (a.students || []).some(s => s.id === student.id))
          .map(a => a.id)
          .sort()

        const answered = selectedIds.length > 0
        if (answered) {
          totalCount = 1
          const sameLength = selectedIds.length === correctIds.length
          const allMatch = sameLength && selectedIds.every((id, idx) => id === correctIds[idx])
          if (!allMatch) {
            wrongCount = 1
            isWrongQuestion = true
          }
        }
      }

      if (innerTest.testType === TestType.TrueFalse) {
        for (const answer of (innerTest.answers || [])) {
          const userAnswer = (answer.usersAnswers || []).find(ua => ua.user.id === student.id)
          if (!userAnswer || userAnswer.value === null) continue
          totalCount += 1
          if (userAnswer.isCorrect === false) {
            wrongCount += 1
            isWrongQuestion = true
          }
        }
      }

      if (innerTest.testType === TestType.TextCompletion) {
        const hiddenWords = (innerTest.words || []).filter(w => w.hidden)
        for (const word of hiddenWords) {
          const userAnswer = (word.usersAnswers || []).find(ua => ua.user.id === student.id)
          if (!userAnswer || userAnswer.answer === "" || userAnswer.answer === null) continue
          totalCount += 1
          if (userAnswer.isCorrect === false) {
            wrongCount += 1
            isWrongQuestion = true
          }
        }
      }

      const wrongPercentage = totalCount > 0 ? Math.round((wrongCount / totalCount) * 100) : 0

      questionStats.push({
        id: innerTest.id,
        title: getQuestionTitle(innerTest, i),
        wrongCount,
        totalCount,
        wrongPercentage,
      })

      if (isWrongQuestion) {
        wrongQuestionIds.push(innerTest.id)
      }

      if (totalCount > 0) {
        for (const tag of (innerTest.tags || [])) {
          const key = tag.id ?? tag.name
          if (!key) continue

          if (!tagStatsMap.has(key)) {
            tagStatsMap.set(key, {
              id: key,
              name: tag.name || "Senza nome",
              wrongCount: 0,
              totalCount: 0,
            })
          }

          const current = tagStatsMap.get(key)
          current.wrongCount += wrongCount
          current.totalCount += totalCount
        }
      }
    }

    const worstQuestions = questionStats
      .filter(q => q.totalCount > 0)
      .sort((a, b) => {
        if (b.wrongPercentage !== a.wrongPercentage) return b.wrongPercentage - a.wrongPercentage
        return b.wrongCount - a.wrongCount
      })
      .slice(0, 5)

    const worstTags = Array.from(tagStatsMap.values())
      .map(tag => ({
        ...tag,
        wrongPercentage: tag.totalCount > 0 ? Math.round((tag.wrongCount / tag.totalCount) * 100) : 0,
      }))
      .sort((a, b) => {
        if (b.wrongPercentage !== a.wrongPercentage) return b.wrongPercentage - a.wrongPercentage
        return b.wrongCount - a.wrongCount
      })
      .slice(0, 5)

    return {
      questions: worstQuestions,
      tags: worstTags,
      wrongQuestionIds,
    }
  }, [student.id, test])

  const filteredTest = useMemo(() => {
    if (!showOnlyWrongQuestions) return test
    return test.filter(innerTest => studentAnalysis.wrongQuestionIds.includes(innerTest.id))
  }, [showOnlyWrongQuestions, studentAnalysis.wrongQuestionIds, test])

  const exportStudentResponsesCsv = useCallback(() => {
    const csvEscape = (value) => {
      const str = value === null || value === undefined ? "" : String(value)
      return `"${str.replace(/"/g, '""')}"`
    }

    const getQuestionTitle = (innerTest, index) => {
      const text = (innerTest.text || "").trim()
      return text.length > 0 ? text : `Domanda ${index + 1}`
    }

    const rows = [[
      "Domanda",
      "Tipo",
      "Tag",
      "Voce",
      "Risposta studente",
      "Risposta corretta",
      "Esito",
    ]]

    for (let i = 0; i < filteredTest.length; i++) {
      const innerTest = filteredTest[i]
      const questionTitle = getQuestionTitle(innerTest, i)
      const tags = (innerTest.tags || []).map(tag => tag.name).join(" | ")

      if (innerTest.testType === TestType.SingleChoice || innerTest.testType === TestType.MultipleChoice) {
        const selectedAnswers = (innerTest.answers || []).filter(a => (a.students || []).some(s => s.id === student.id))
        const correctAnswers = (innerTest.answers || []).filter(a => a.isCorrect)
        const selectedText = selectedAnswers.map(a => a.text).join(" | ") || "Nessuna risposta"
        const correctText = correctAnswers.map(a => a.text).join(" | ")

        const selectedIds = selectedAnswers.map(a => a.id).sort()
        const correctIds = correctAnswers.map(a => a.id).sort()
        const sameLength = selectedIds.length === correctIds.length
        const isCorrect = sameLength && selectedIds.every((id, idx) => id === correctIds[idx])

        rows.push([
          questionTitle,
          innerTest.testType === TestType.SingleChoice ? "SingleChoice" : "MultipleChoice",
          tags,
          "Risposta",
          selectedText,
          correctText,
          isCorrect ? "Corretta" : "Sbagliata",
        ])
      }

      if (innerTest.testType === TestType.TrueFalse) {
        for (const answer of (innerTest.answers || [])) {
          const userAnswer = (answer.usersAnswers || []).find(ua => ua.user.id === student.id)
          const studentValue = userAnswer?.value
          const studentText = studentValue === null || studentValue === undefined ? "Nessuna risposta" : (studentValue ? "Vero" : "Falso")
          const correctText = answer.value ? "Vero" : "Falso"
          const outcome = userAnswer?.isCorrect === true ? "Corretta" : "Sbagliata"

          rows.push([
            questionTitle,
            "TrueFalse",
            tags,
            answer.text || "Affermazione",
            studentText,
            correctText,
            outcome,
          ])
        }
      }

      if (innerTest.testType === TestType.TextCompletion) {
        const hiddenWords = (innerTest.words || []).filter(w => w.hidden)
        for (const word of hiddenWords) {
          const userAnswer = (word.usersAnswers || []).find(ua => ua.user.id === student.id)
          const studentText = userAnswer?.answer || "Nessuna risposta"
          const correctText = (word.solutions || []).join(" | ")
          const outcome = userAnswer?.isCorrect === true ? "Corretta" : "Sbagliata"

          rows.push([
            questionTitle,
            "TextCompletion",
            tags,
            word.text || "Parola",
            studentText,
            correctText,
            outcome,
          ])
        }
      }
    }

    const csvContent = rows
      .map(row => row.map(csvEscape).join(";"))
      .join("\n")

    const blob = new Blob([`\uFEFF${csvContent}`], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    const safeName = `${student.name || "studente"}_${student.surname || ""}`.trim().replace(/\s+/g, "_")
    link.href = url
    link.download = `risposte_${safeName}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [filteredTest, student.id, student.name, student.surname])

  return (
    <div className={styles.container}>
      <Back onClick={onBack} />

      <div className={styles.top}>
        <Card style={{ flexGrow: 1 }}>
          <div className={styles.studentCard}>
            <div className={styles.cardTitle}>{t("tests.student")}</div>
            <div className={styles.student}>
              <img className={styles.picture} src={student.picture ?? UserPlaceholder} alt="" />
              <div className={styles.name}>{student.name} {student.surname}</div>
              <Button
                accentColor="var(--tertiary)"
                onClick={onContact}
                style={{ padding: "0.5rem 1rem" }}
              >
                <ChatIcon style={{ width: "24px", height: "24px" }} />
                {t("tests.contact").toUpperCase()}
              </Button>
            </div>

          </div>
        </Card>
        <Card style={{ flexGrow: 1 }}>
          <div className={styles.recap}>
            <div className={styles.cardTitle}>{t("tests.recapTitle")}</div>
            <div className={styles.circularChartColumn}>
              <CircularProgress content={circularContent} data={circularData.filter(d => d.id !== "notAnswered")}></CircularProgress>
              <div className={styles.chartLabels}>
                {circularData.map(data => {
                  return (
                    <div className={styles.chartCell} key={data.label}>
                      <div className={styles.chartRow}>
                        <div className={styles.chartColumn}>
                          <div className={styles.chartColor} style={{ backgroundColor: data.color }}></div>
                        </div>
                        <div className={styles.chartColumn}>
                          <div className={styles.chartDataTitle}>{data.rawValue}</div>
                          <div className={styles.chartDataSubitle} style={{ color: data.color }}>{data.label}</div>
                        </div>
                      </div>
                    </div>
                  )
                })}
                <div className={styles.chartCell}>
                  <div className={styles.chartRow}>
                    <div className={styles.chartColumn}>
                      <div className={styles.chartColor} style={{ backgroundColor: 'var(--secondary)' }}></div>
                    </div>
                    <div className={styles.chartColumn}>
                      <div className={styles.chartDataTitle}>{formatSeconds(student.duration, true)}</div>
                      <div className={styles.chartDataSubitle} style={{ color: 'var(--secondary)' }}>Tempo</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      <div className={styles.content}>
        <Card style={{ flexGrow: 1 }}>
          <div className={styles.analysisContainer}>
            <div className={styles.analysisHeader}>
              <div className={styles.cardTitle}>Analisi Studente</div>
              <div className={styles.analysisActions}>
                <Button
                  accentColor="var(--secondary)"
                  onClick={exportStudentResponsesCsv}
                  appearance="subtle"
                >
                  ESPORTA RISULTATI
                </Button>
                <Button
                appearance="subtle"
                  inverse={showOnlyWrongQuestions}
                  accentColor={showOnlyWrongQuestions ? "var(--sf-red)" : "var(--tertiary)"}
                  onClick={() => setShowOnlyWrongQuestions(prev => !prev)}
                  style={{ padding: "0.45rem 0.8rem" }}
                >
                  {showOnlyWrongQuestions ? "MOSTRA TUTTE" : "MOSTRA DOMANDE ERRATE"}
                </Button>
              </div>
            </div>

            <div className={styles.analysisGrid}>
              <div className={styles.analysisColumn}>
                <div className={styles.analysisTitle}>Argomenti piu sbagliati</div>
                {
                  studentAnalysis.tags.length === 0 &&
                  <div className={styles.analysisEmpty}>Nessun tag disponibile</div>
                }
                <Each
                  of={studentAnalysis.tags}
                  render={(item, index) => (
                    <div className={styles.analysisRow} key={`st-${item.id}`}>
                      <div className={styles.analysisRank}>{index + 1}.</div>
                      <div className={styles.analysisText}>{item.name}</div>
                      <div className={styles.analysisValue}>{item.wrongPercentage}%</div>
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        </Card>

        {
          showOnlyWrongQuestions && filteredTest.length === 0 &&
          <Card style={{ flexGrow: 1 }}>
            <div className={styles.noWrongQuestions}>Nessuna domanda sbagliata da mostrare</div>
          </Card>
        }

        <Each
          of={filteredTest}
          render={(innerTest) => (
            <Card style={{ flexGrow: 1, padding: 0 }}>
              <div className={styles.innerTest}>

                {
                  innerTest.text &&
                  <div className={styles.cardTitle} style={{
                    paddingTop: "1rem",
                  }}>{innerTest.text}</div>
                }
                {
                  Array.isArray(innerTest.tags) && innerTest.tags.length > 0 &&
                  <div className={styles.questionTags}>
                    <Each
                      of={innerTest.tags}
                      render={(tag) => (
                        <Tag key={`${innerTest.id}-${tag.id ?? tag.name}`} tag={tag} />
                      )}
                    />
                  </div>
                }
                {
                  (innerTest.testType === TestType.SingleChoice || innerTest.testType === TestType.MultipleChoice) &&
                  <Each
                    of={innerTest.answers}
                    render={(answer, index) => {
                      const isStudentAnswer = answer.students.map((s) => s.id).includes(student.id)
                      let resultBadge = null
                      let answerClass = answer.isCorrect ? `${styles.answer} ${styles.correct}` : styles.answer
                      if (isStudentAnswer && answer.isCorrect) {
                        resultBadge = {
                          label: t("tests.correct"),
                          Icon: CheckIcon,
                          class: `${styles.badge} ${styles.correct}`
                        }

                      } else if (isStudentAnswer && !answer.isCorrect) {
                        answerClass = `${styles.answer} ${styles.wrong}`
                        resultBadge = {
                          label: t("tests.wrong"),
                          Icon: CrossIcon,
                          class: `${styles.badge} ${styles.wrong}`
                        }
                      }

                      return (
                        <div className={styles.answerContainer}>
                          <div className={styles.answerWrapper}>
                            <div className={answerClass}>
                              <div className={styles.left}>
                                <div className={answer.isCorrect ? styles.correctLabel : styles.wrongLabel}>{toLetter(index + 1).toUpperCase()}</div>
                                <ReactMarkdown children={answer.text} />
                              </div>
                              {
                                resultBadge !== null &&
                                <div className={resultBadge.class}>{resultBadge.label} <resultBadge.Icon style={{ width: "16px", height: "16px" }} /></div>
                              }
                            </div>

                          </div>
                        </div>
                      )
                    }}
                  />
                }
                {
                  innerTest.testType === TestType.TrueFalse &&
                  <Each
                    of={innerTest.answers}
                    render={(answer) => (
                      <div className={styles.answerContainer} >
                        <div className={styles.answerWrapper}>
                          <div className={answer.usersAnswers.find(ua => ua.user.id === student.id)?.isCorrect ? styles.answer : `${styles.answer} ${styles.wrong}`}>
                            <ReactMarkdown children={answer.text} />
                            <div className={styles.badges}>
                              <TrueFalseBadge type={true} onChange={null} value={answer.value} />
                              <TrueFalseBadge type={false} onChange={null} value={answer.value} />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  />
                }
                {
                  innerTest.testType === TestType.TextCompletion &&
                  <div className={styles.textCompletion}>
                    <div className={styles.words}>
                      <Each
                        of={innerTest.words}
                        render={(word) => {
                          if (!word.hidden) {
                            return <div className={styles.word}>{word.text}</div>
                          } else {
                            const userAnswer = word.usersAnswers.find(ua => ua.user.id === student.id)
                            if (word.hidden && !userAnswer.isCorrect) {
                              return <div className={`${styles.hiddenWord} ${styles.wrong}`}><span>{userAnswer.answer}</span> <span>{word.solutions[0]}</span></div>
                            } else if (word.hidden && userAnswer.isCorrect) {
                              return <div className={styles.hiddenWord}>{userAnswer.answer}</div>
                            }
                          }
                        }}
                      />
                    </div>
                    <div className={styles.solutions}>
                      <Each
                        of={innerTest.words.filter(w => w.hidden)}
                        render={(hiddenWord, index) => (
                          <div className={styles.solutionContainer}>
                            <div>{`${index + 1}. `}{hiddenWord.usersAnswers.find(ua => ua.user.id === student.id).answer}</div>
                            {hiddenWord.usersAnswers.find(ua => ua.user.id === student.id).answer && "->"}
                            <div className={styles.solution}>{hiddenWord.solutions.join(", ")}</div>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                }
              </div>
            </Card>

          )}
        />
      </div>
    </div >
  )

}

export default StudentTestResult
